import type { useGlobalDrawerStack, useGlobalModalStack } from "@west-tennessee-consulting/ui";
import type { BaseEntity, SimpleTableConfig, EntityHooks } from "../../UniversalCRMTable/types";
import { useCrudTriggerHandlers } from "./useCrudTriggerHandlers";
import { useInlineEditHandler } from "./useInlineEditHandler";
import { useCrudMutationHandlers } from "./useCrudMutationHandlers";
import { useBulkActionHandlers } from "./useBulkActionHandlers";

export type { InlineEditPayload } from "./crudHandlerTypes";

/**
 * CRUD event handlers for UniversalEntityPage.
 *
 * Composes focused sub-handlers in this directory:
 * - useCrudTriggerHandlers — modal/drawer open/close + view navigation
 * - useInlineEditHandler — per-row inline edit logic
 * - useCrudMutationHandlers — create/update/delete mutation flows
 * - useBulkActionHandlers — bulk action wiring
 */
export function useCrudHandlers<T extends BaseEntity>(
  entityName: string,
  config: SimpleTableConfig<T>,
  isMobile: boolean,
  hooks: EntityHooks<T>,
  globalModalStack: ReturnType<typeof useGlobalModalStack>,
  drawerStack: ReturnType<typeof useGlobalDrawerStack>,
  editingItem: T | null,
  setEditingItem: (item: T | null) => void,
  deletingItem: T | null,
  setDeletingItem: (item: T | null) => void,
  canCreate: boolean,
  refetch: () => void
) {
  const createMutation = hooks.useCreate();
  const updateMutation = hooks.useUpdate();
  const deleteMutation = hooks.useDelete();

  const triggers = useCrudTriggerHandlers<T>(
    entityName,
    config,
    isMobile,
    globalModalStack,
    drawerStack,
    setEditingItem,
    setDeletingItem,
    canCreate
  );

  const handleInlineEdit = useInlineEditHandler<T>(config, updateMutation, refetch);

  const mutations = useCrudMutationHandlers<T>(
    entityName,
    config,
    isMobile,
    createMutation,
    updateMutation,
    deleteMutation,
    globalModalStack,
    drawerStack,
    editingItem,
    setEditingItem,
    deletingItem,
    setDeletingItem,
    refetch
  );

  const { bulkActions, handleBulkAction } = useBulkActionHandlers<T>(
    entityName,
    updateMutation,
    deleteMutation
  );

  return {
    handleCreateItem: triggers.handleCreateItem,
    handleEditItem: triggers.handleEditItem,
    handleInlineEdit,
    handleDeleteItem: triggers.handleDeleteItem,
    handleViewItem: triggers.handleViewItem,
    handleItemCreated: mutations.handleItemCreated,
    handleItemUpdated: mutations.handleItemUpdated,
    handleItemDeleted: mutations.handleItemDeleted,
    handleImportItems: triggers.handleImportItems,
    handleCancelDelete: triggers.handleCancelDelete,
    bulkActions,
    handleBulkAction,
  };
}
